import React, { useState, useEffect } from 'react';
import CommonModal from '../CommonModal';
import Button from '../Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { CheckCircle2, XCircle, ShoppingBag } from 'lucide-react';
import { RoutesLink } from '@/utility/RouteLinks';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RootState, useAppSelector, useAppDispatch } from '@/redux/store';
import { fetchLiveOrders, fetchUserDetails } from '@/redux/features/UserSlice';

interface OrderNotificationModalProps {
    open: boolean;
    onClose: () => void;
    payload: any;
}

export const OrderNotificationModal: React.FC<OrderNotificationModalProps> = ({ open, onClose, payload }) => {
    const navigate = useRoleNavigate();
    const [cancelReason, setCancelReason] = useState('');
    const [isCancelling, setIsCancelling] = useState(false);
    const dispatch = useAppDispatch();
    const loadOrders = async () => {
        await dispatch(fetchLiveOrders());
    };

    const { user } = useAppSelector((state: RootState) => state.user);
    const [notifyDriver, setNotifyDriver] = useState(
        user?.storeProfile?.settings?.isDriverNotificationEnabled ?? false
    );
    const [isToggleLoading, setIsToggleLoading] = useState(false);

    const handleNotificationToggle = async (checked: boolean) => {
        setNotifyDriver(checked);
        setIsToggleLoading(true);
        try {
            const formData = new FormData();
            formData.append('isDriverNotificationEnabled', String(checked));
            await callApi(
                `${API_URL}${API_ENDPOINT.updateStore}`,
                { method: 'PUT', data: formData, multipart: true }
            );
            dispatch(fetchUserDetails());
            showToastNotification('success', 'Driver notification setting updated');
        } catch (error) {
            ThrowError(error);
            setNotifyDriver(!checked);
        } finally {
            setIsToggleLoading(false);
        }
    };

    useEffect(() => {
        if (!open) {
            setIsCancelling(false);
            setCancelReason('');
        }
    }, [open]);

    const handleStatusUpdate = async (newStatus: string) => {
        if (!orderId) {
            showToastNotification('danger', 'Cannot perform action: Order ID is missing from notification data.');
            return;
        }

        if (newStatus === 'cancelled' && !isCancelling) {
            setIsCancelling(true);
            return;
        }

        if (newStatus === 'cancelled' && isCancelling && !cancelReason.trim()) {
            showToastNotification('danger', 'Please provide a reason for rejection.');
            return;
        }

        setLoading(true);
        try {
            const data: any = { id: orderId, status: newStatus };
            if (newStatus === 'cancelled') {
                data.cancelledReason = cancelReason.trim();
            }

            await callApi(
                `${API_URL}${API_ENDPOINT.updateOrderStatus}`,
                { method: 'PATCH', data }
            );
            showToastNotification('success', `Order status updated to ${newStatus}`);
            onClose();
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
            loadOrders();
        }
    };

    const [loading, setLoading] = useState(false);
    const title = payload?.notification?.title || 'New Order Received';
    const body = payload?.notification?.body || 'You have received a new order.';
    const orderId = payload?.data?.id || payload?.data?.orderId || payload?.data?.order_id;
    const deliveryType = payload?.data?.deliveryType;

    const handleViewOrder = () => {
        if (orderId) {
            navigate(RoutesLink.ViewOrders.replace(':id', orderId));
        } else {
            navigate(RoutesLink.Orders);
        }
        onClose();
    };

    return (
        <CommonModal open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }} title="New Order Notification" size="md">
            <div className="flex flex-col items-center justify-center text-center p-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <ShoppingBag className="w-8 h-8 text-primary" />
                </div>

                <h3 className="text-lg font-bold text-foreground mb-2">
                    {title}
                </h3>

                <p className="text-sm text-muted-foreground mb-6">
                    {body}
                </p>

                <div className="w-full space-y-4">
                    {orderId ? (
                        <>
                            {!isCancelling ? (
                                <>
                                    {deliveryType !== 'takeaway' && (
                                        <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5 mb-4 text-left">
                                            <div className="space-y-0.5">
                                                <Label className="text-sm font-medium">Notify Drivers</Label>
                                                <p className="text-[10px] text-muted-foreground leading-tight">Automatically alert nearby drivers</p>
                                            </div>
                                            <Switch
                                                checked={notifyDriver}
                                                onCheckedChange={handleNotificationToggle}
                                                disabled={isToggleLoading}
                                            />
                                        </div>
                                    )}
                                    <Button
                                        variant="success"
                                        fullWidth
                                        leftIcon={<CheckCircle2 className="w-4 h-4" />}
                                        isLoading={loading}
                                        disabled={isToggleLoading || loading}
                                        onClick={() => handleStatusUpdate('accepted')}
                                    >
                                        Accept
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        fullWidth
                                        leftIcon={<XCircle className="w-4 h-4" />}
                                        disabled={isToggleLoading || loading}
                                        onClick={() => handleStatusUpdate('cancelled')}
                                    >
                                        Reject Order
                                    </Button>
                                </>
                            ) : (
                                <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="text-left">
                                        <label className="text-xs font-bold text-foreground mb-1 block uppercase tracking-wider">Reason for Rejection</label>
                                        <textarea
                                            className="w-full rounded-xl border border-border bg-background p-3 text-sm focus:ring-1 focus:ring-primary/20 focus:border-primary/30 outline-none transition-all resize-none"
                                            rows={3}
                                            placeholder="e.g. Out of stock, Store closing soon..."
                                            value={cancelReason}
                                            onChange={(e) => setCancelReason(e.target.value)}
                                        />
                                    </div>
                                    <Button
                                        variant="destructive"
                                        fullWidth
                                        isLoading={loading}
                                        onClick={() => handleStatusUpdate('cancelled')}
                                    >
                                        Confirm Rejection
                                    </Button>
                                    <button
                                        type="button"
                                        className="text-xs text-muted-foreground hover:text-foreground font-medium underline underline-offset-4"
                                        onClick={() => setIsCancelling(false)}
                                    >
                                        Wait, keep order
                                    </button>
                                </div>
                            )}

                            {!isCancelling && (
                                <Button
                                    variant="outline"
                                    fullWidth
                                    disabled={loading}
                                    onClick={handleViewOrder}
                                >
                                    View Order Details
                                </Button>
                            )}
                        </>
                    ) : (
                        <Button
                            variant="primary"
                            fullWidth
                            onClick={handleViewOrder}
                        >
                            Go to Orders
                        </Button>
                    )}
                </div>
            </div>
        </CommonModal>
    );
};
